import { useEffect, useRef, useState } from 'react'
import { Button } from '@/shared/ui'
import { useCallStore, type LiveTurn, type Speaker } from '../model'
import styles from './CallStage.module.css'

interface CallStageProps {
  onHangUp: () => void
}

/**
 * Живой разговор: кто говорит, что уже сказано, где перебили.
 *
 * Реплики появляются по мере генерации, а не после её конца. Разница
 * заметная: пока агент думает и говорит, человеку видно, что процесс
 * идёт, — иначе полторы секунды тишины читаются как зависание.
 */
export function CallStage({ onHangUp }: CallStageProps) {
  const { status, detail, speaker, turns, micLevel, agentLevel, startedAt } = useCallStore()
  const feedRef = useRef<HTMLDivElement>(null)

  // Лента едет за последней репликой. Только если человек не отлистал
  // вверх сам: утащить его от места, которое он читает, — худшее, что
  // может сделать автопрокрутка.
  useEffect(() => {
    const feed = feedRef.current
    if (!feed) return

    const atBottom = feed.scrollHeight - feed.scrollTop - feed.clientHeight < 120
    if (atBottom) feed.scrollTop = feed.scrollHeight
  }, [turns])

  const level = speaker === 'human' ? micLevel : agentLevel

  return (
    <div className={styles.stage}>
      <header className={styles.head}>
        <div className={orbClass(speaker)}>
          {/* Кольцо раздувается от громкости, с потолком: близкий
              микрофон иначе разносит его на пол-экрана. */}
          <span
            className={styles.pulse}
            style={{ transform: `scale(${1 + Math.min(level * 2.2, 0.65)})` }}
          />
          {speaker === 'human' ? <MicIcon /> : <WaveIcon />}
        </div>

        <div className={styles.headText}>
          <span className={styles.who}>{speakerTitle(speaker, status)}</span>
          <span className={styles.hint}>{speakerHint(speaker, status)}</span>
        </div>

        <Timer startedAt={startedAt} />
      </header>

      {status === 'error' && detail && <div className={styles.error}>{detail}</div>}

      <div className={styles.feed} ref={feedRef}>
        {turns.length === 0 ? (
          <p className={styles.empty}>
            Агент начнёт первым. Отвечайте как обычный человек — можно перебивать на
            полуслове, он это услышит.
          </p>
        ) : (
          turns.map((turn) => <Bubble key={turn.id} turn={turn} />)
        )}
      </div>

      <div className={styles.controls}>
        <Button variant="danger" onClick={onHangUp}>
          Положить трубку
        </Button>
      </div>
    </div>
  )
}

function Bubble({ turn }: { turn: LiveTurn }) {
  const human = turn.role === 'human'

  // Пока реплика пишется, показываем накопленный текст. Когда закончена
  // и перебита — услышанную часть отдельно от оборванного хвоста.
  const heard = turn.interrupted && turn.heard ? turn.heard : turn.text
  const tail = turn.interrupted && turn.heard ? tailOf(turn) : ''

  return (
    <div className={`${styles.row} ${human ? styles.rowHuman : ''}`}>
      <div className={`${styles.bubble} ${human ? styles.human : styles.agent}`}>
        {turn.streaming && !turn.text ? (
          <span className={styles.thinking}>
            <span className={styles.dot} />
            <span className={styles.dot} />
            <span className={styles.dot} />
          </span>
        ) : (
          <>
            {heard}
            {tail && <span className={styles.tail}> {tail}</span>}
            {turn.interrupted && <span className={styles.cut}>перебили здесь</span>}
            {turn.streaming && <span className={styles.caret} />}
          </>
        )}

        {!human && !turn.streaming && turn.replyMs > 0 && (
          <div className={`${styles.meta} ${turn.replyMs > 1500 ? styles.metaSlow : ''}`}>
            ответил через {(turn.replyMs / 1000).toFixed(2)} с
          </div>
        )}
      </div>
    </div>
  )
}

/** Оборванный хвост: то, чего собеседник не услышал. */
function tailOf(turn: LiveTurn): string {
  if (!turn.text.startsWith(turn.heard)) return ''
  return turn.text.slice(turn.heard.length).trim()
}

function Timer({ startedAt }: { startedAt: number | null }) {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (!startedAt) return
    const id = window.setInterval(() => setNow(Date.now()), 500)
    return () => window.clearInterval(id)
  }, [startedAt])

  if (!startedAt) return <span className={styles.timer}>00:00</span>

  const seconds = Math.floor((now - startedAt) / 1000)
  const mm = String(Math.floor(seconds / 60)).padStart(2, '0')
  const ss = String(seconds % 60).padStart(2, '0')

  return <span className={styles.timer}>{`${mm}:${ss}`}</span>
}

function orbClass(speaker: Speaker): string {
  if (speaker === 'agent') return `${styles.orb} ${styles.orbAgent}`
  if (speaker === 'human') return `${styles.orb} ${styles.orbHuman}`
  return styles.orb ?? ''
}

function speakerTitle(speaker: Speaker, status: string): string {
  if (status === 'connecting') return 'Соединяем'
  if (status === 'error') return 'Разговор прерван'
  if (speaker === 'agent') return 'Говорит агент'
  if (speaker === 'human') return 'Слушает вас'
  if (speaker === 'thinking') return 'Думает'
  return 'Тишина'
}

function speakerHint(speaker: Speaker, status: string): string {
  if (status === 'connecting') return 'Разрешите доступ к микрофону'
  if (speaker === 'agent') return 'Можно перебивать — он уступит'
  if (speaker === 'human') return 'Слышит вас'
  if (speaker === 'thinking') return 'Подбирает ответ'
  return 'Скажите что-нибудь'
}

function MicIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <rect x="7" y="2.5" width="6" height="9" rx="3" stroke="currentColor" strokeWidth="1.7" />
      <path
        d="M4.5 9.5C4.5 12.5 7 14.5 10 14.5C13 14.5 15.5 12.5 15.5 9.5M10 14.5V17.5"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  )
}

function WaveIcon() {
  return (
    <svg width="22" height="16" viewBox="0 0 22 16" fill="none" aria-hidden="true">
      <path
        d="M2 8H4M6.5 4V12M11 1.5V14.5M15.5 4V12M20 8H18"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
      />
    </svg>
  )
}
